import {
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectVirtualCardIdFromForegroundRoute } from 'src/app/ShareBillPayVirtualCardPopup/redux/shareBillPayVirtualCard.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { CreateBillPayVirtualCardToastMessage } from 'src/app/Toast/Toast'
import Button from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Popup from 'src/components/Popup/Popup'
import SvgEmail from 'src/components/Svgs/SvgEmail'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import VirtualCardDetailImage from 'src/components/VirtualCardDetailImage/VirtualCardDetailImage'
import { useFlyoutDispatch, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import { closeModal, connectedPush } from 'src/navigation/Lnk'
import { virtualCardDetailUrl } from 'src/navigation/urls'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  usePostShareBillPayVirtualCard,
  useQueryVirtualCardDetail,
  useQueryVirtualCardPermissions,
} from 'src/queries/virtualCards.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { stringIsValidEmail } from 'src/utils/validation/string.validation'
import styled, { css } from 'styled-components'

type ShareBillPayVirtualCardFlatFormValues = {
  vendorEmail?: string
}

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 32px;
`

const CardSection = styled.div`
  display: flex;
  overflow: hidden;
  align-items: stretch;
  gap: 24px;
  padding: 28px 20px;
  margin: 32px 0;
  background-color: ${styleConstants.colors.gray_lightest};
  border-radius: ${styleConstants.borderRadius.input};
  border: 1px solid ${styleConstants.colors.gray_medium};
`
const DetailsSection = styled.div`
  width: 100%;
  max-width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
`
const ErrorRow = styled.div`
  margin-top: 32px;
  ${getFontCss(
    14,
    styleConstants.colors.red_darkest,
    styleConstants.fontFamilies.regular,
  )}
`

const LabelValue: React.FC<{
  labelSource: string
  value: string | React.ReactElement
}> = (props) => {
  const translate = useTranslate()
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 2px;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {translate(props.labelSource)}
      </span>
      <span
        css={css`
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {props.value}
      </span>
    </div>
  )
}

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 32px;
  div {
    width: 50%;
  }
`

const ShareBillPayVirtualCardPopup: React.FC = () => {
  const translate = useTranslate()

  const enableBillPayShareLater = useSelector(
    selectFeatureFlaggerFromState,
  ).show_bill_pay_share_later

  const locale = useSelector(selectNullSafeLocaleFromState)
  const dispatch = useFlyoutDispatch()
  const virtualCardId = useSelector(selectVirtualCardIdFromForegroundRoute)
  const { grayscaleImageEnabled } = useSelector(selectBrandFeaturesFromState)
  const { data: user } = useQueryOwnUser({})
  const { data: virtualCardPermissions, isLoading: permissionsLoading } =
    useQueryVirtualCardPermissions({ virtualCardId, retry: false })
  const { data: virtualCard, isLoading: virtualCardLoading } =
    useQueryVirtualCardDetail({
      virtualCardId,
      enabled: virtualCardPermissions !== undefined,
    })

  const {
    mutate: postShareVirtualCard,
    isPending: postShareVirtualCardLoading,
    error: postShareVirtualCardError,
  } = usePostShareBillPayVirtualCard()

  const {
    fields,
    fieldsErrors,
    setFieldsErrors,
    onFieldChange,
    setErrorsAndReturnDidPassValidation: fieldsSetErrors,
  } = useFormFields<ShareBillPayVirtualCardFlatFormValues>({}, () => ({
    vendorEmail: [stringIsValidEmail()],
  }))
  useEffect(() => {
    // if we've determined the card is not shareable, exit out of this modal before it presents anything
    if (
      !enableBillPayShareLater ||
      (!permissionsLoading &&
        !virtualCardLoading &&
        user &&
        !virtualCard?.isBillPayShareable(user.id))
    ) {
      dispatch(closeModal())
    }
  }, [permissionsLoading, virtualCardLoading, user, virtualCard])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (fieldsSetErrors() && virtualCardId && fields.vendorEmail) {
      postShareVirtualCard(
        { virtualCardId, body: { vendorEmail: fields.vendorEmail } },
        {
          onSuccess: ({ virtualCard }, { virtualCardId }) => {
            dispatch(connectedPush(virtualCardDetailUrl(virtualCardId)))
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <CreateBillPayVirtualCardToastMessage
                    virtualCard={new VirtualCardSecureModel(virtualCard)}
                    shared={true}
                  />
                ),
              }),
            )
          },
        },
      )
    }
  }
  return (
    <>
      {user &&
        virtualCard?.billPayMeta &&
        virtualCard.isBillPayShareable(user.id) && (
          <Popup hideExit>
            <div
              css={css`
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-bottom: 20px;
              `}
            >
              {translate('Securely share card with vendor')}
            </div>
            {translate(
              'Share this virtual card with your vendor in order to complete your invoice payment. The card will be sent via email with a secure link.',
            )}
            <CardSection>
              <VirtualCardDetailImage
                css={css`
                  align-self: flex-start;
                  max-width: 50%;
                `}
                recipient={user}
                virtualCard={virtualCard}
                grayscaleImage={grayscaleImageEnabled}
              />
              <DetailsSection>
                <LabelValue
                  labelSource={'Vendor Name'}
                  value={virtualCard.billPayMeta.vendorName}
                />
                <LabelRow>
                  <LabelValue
                    labelSource={'Invoice Amount'}
                    value={virtualCard.limitLabel}
                  />
                  <LabelValue
                    labelSource={'Invoice Number'}
                    value={virtualCard.billPayMeta.invoiceNumber ?? '--'}
                  />
                </LabelRow>
                <LabelRow>
                  <LabelValue
                    labelSource={'Invoice Date'}
                    value={virtualCard.invoiceDateTimezonedString ?? '--'}
                  />
                  <LabelValue
                    labelSource={'Active Until'}
                    value={virtualCard.activeUntilOrPseudoStatusLabel}
                  />
                </LabelRow>
              </DetailsSection>
            </CardSection>
            <form onSubmit={onSubmit}>
              <TextInputField
                fieldLabel={translate('Vendor Email')}
                value={fields.vendorEmail}
                errors={fieldsErrors.vendorEmail}
                onChange={(val: string): void => {
                  onFieldChange('vendorEmail', val)
                  setFieldsErrors({ vendorEmail: undefined })
                }}
                placeholder={translateSingleString(
                  locale,
                  'Enter ${fieldLabel}',
                  { fieldLabel: translateSingleString(locale, 'vendor email') },
                )}
                design={TextInputFieldDesigns.New}
                leftImage={
                  <SvgEmail
                    fill={
                      fieldsErrors.vendorEmail
                        ? styleConstants.colors.red_darkest
                        : 'var(--color-cta)'
                    }
                  />
                }
              />
              <ButtonRow>
                <Button
                  testId={'shareBillPayVirtualCardShareBtn'}
                  type={ButtonTypes.submit}
                  design={ButtonDesigns.designCta}
                  big={true}
                  text={translate('Share and Complete Payment')}
                  loading={postShareVirtualCardLoading}
                  disabled={postShareVirtualCardLoading}
                />
                <HoverableLink
                  testId={'shareBillPayVirtualCardKeepBtn'}
                  ariaLabelSource={'Keep for myself'}
                  css={`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    height: 32px;
                    ${getFontCss(
                      14,
                      themeConstants.colors.cta_dark,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                  onClick={(): void => {
                    dispatch(closeModal())
                  }}
                >
                  {translate('Keep for myself')}
                </HoverableLink>
              </ButtonRow>
              {postShareVirtualCardError && (
                <ErrorRow>{postShareVirtualCardError?.error}</ErrorRow>
              )}
            </form>
          </Popup>
        )}
    </>
  )
}

export default ShareBillPayVirtualCardPopup
